/**
 * Detects image URLs that likely contain embedded publisher branding.
 * Used at render-time to swap branded images for neutral category fallbacks.
 *
 * Detection is URL-based (no runtime image analysis):
 *   1. Publisher CDN hostnames confirmed to watermark all thumbnails
 *   2. URL path/query patterns: OG templates, logos, placeholders, channel directories
 *   3. Explicitly tiny dimensions in URL (< 100 px) — icons, tracking pixels
 */

// Publisher CDN hostnames confirmed to embed their logo into every article thumbnail.
const BRANDED_DOMAINS = [
  'navhindtimes.com',
  'abplive.com',                // ABP Live — logo watermark on all thumbnails (all subdomains)
  'img.aajtak.in',              // Aaj Tak — logo overlay on all thumbnails
  'resize.indiatv.in',          // India TV — resize service stamps channel logo
  'images.tv9hindi.com',        // TV9 Hindi image CDN
  'images.tv9bharatvarsh.com',  // TV9 Bharatvarsh image CDN
];

// URL path/query substrings indicating a branded, placeholder, or non-article image.
const BRANDED_PATH_PATTERNS = [
  // Open Graph / social-share templates — always carry publisher branding
  'og-image', 'og_image', 'opengraph-image', 'opengraph_image',
  'social-share', 'social_share', 'social-image', 'social_image',
  'preview-image', 'preview_image',

  // Explicitly watermarked images
  'watermark',

  // Placeholder / default images
  'noimage', 'no-image', 'no_image',
  'default-thumb', 'default_thumb', 'default-image', 'default_image',
  '/placeholder',

  // Logo / brand assets in URL path
  '/logo/', '/logos/',
  '-logo.', '_logo.', '/logo.',   // e.g. site-logo.jpg, logo.png, prefix-logo.png
  'brand-image', 'brand_image',
  'channel-logo', 'station-logo',
  '/branding/',

  // Channel-branded directories served via shared CDNs
  '/abplive/', '/tv9hindi/', '/tv9/', '/aajtak/', '/zeenews/', '/news18/',

  // Tiny utility images
  'favicon', '1x1', '0x0',
];

// Matches WxH pixel patterns in URL paths where both dimensions fit in 1–2 digits (< 100).
// This catches icons like /32x32/ or _48x48. without matching article sizes like 1200x630.
const SMALL_DIM_PATH_RE = /(?:\/|_|-|\b)(\d{1,2})x(\d{1,2})(?:\/|\.|_|-|\b)/;

// Matches small explicit size query params (value 1–99): ?w=48, ?h=32, ?width=16, ?height=64.
const SMALL_DIM_PARAM_RE = /[?&](?:w|h|width|height)=([1-9]\d?)(?:&|$)/;

/**
 * Returns true if the image URL is likely to display embedded publisher branding
 * or is otherwise unsuitable as an article thumbnail (logo, icon, OG template, tiny image).
 * Returns false for data/blob URIs and on URL parse errors.
 */
export const isBrandedImage = (imageUrl) => {
  if (!imageUrl) return false;
  // data: and blob: URIs are uploaded/inline content — never branded
  if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) return false;
  try {
    const url = new URL(imageUrl);
    const hostname = url.hostname.toLowerCase();
    const pathAndQuery = (url.pathname + url.search).toLowerCase();

    // 1. Branded CDN hostname
    if (BRANDED_DOMAINS.some(d => hostname === d || hostname.endsWith('.' + d))) {
      return true;
    }

    // 2. Branded / placeholder path or query pattern
    if (BRANDED_PATH_PATTERNS.some(p => pathAndQuery.includes(p))) {
      return true;
    }

    // 3. Tiny WxH dimensions in path (e.g. /32x32/, _48x48.) — icons and small logos
    const dimPath = SMALL_DIM_PATH_RE.exec(url.pathname.toLowerCase());
    if (dimPath) {
      const w = parseInt(dimPath[1], 10);
      const h = parseInt(dimPath[2], 10);
      if (w < 100 && h < 100) return true;
    }

    // 4. Tiny explicit width/height query param (e.g. ?w=48, ?height=32)
    const dimParam = SMALL_DIM_PARAM_RE.exec(url.search.toLowerCase());
    if (dimParam && parseInt(dimParam[1], 10) < 100) return true;

    return false;
  } catch {
    // Relative or invalid URL — pass through to img onError handler
    return false;
  }
};
